import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import moment from "moment";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContractTemplateDto, ProjectDto, useCreateContractMutation } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { SelectorValue } from "../../../common/input/Selector/SelectorValue";
import { SingleFormSelector } from "../../../common/input/Selector/SingleFormSelector";
import { urls } from "../../../urls";
import { showsContractDepartment } from "../../../common/departments/departmentVisibility";
import {
  contractDepartmentOptions,
  initialContractDepartmentId,
} from "../../../common/departments/eventCreationDepartments";

interface FormValues {
  title: string;
  template: SelectorValue | null;
}

interface Props {
  templates: ContractTemplateDto[];
  project?: ProjectDto;
  goToContractOnCreation?: boolean;
  onClose: () => void;
}

export const CreateContractForm = ({ templates, project, goToContractOnCreation = true, onClose }: Props) => {
  const authState = useLoggedInWithOrgContextState();

  const navigate = useNavigate();
  const toast = useToast();

  const [create, { isLoading }] = useCreateContractMutation();
  const apiErrorDisplayer = useApiError();

  const departments = contractDepartmentOptions(authState.selectedOrg);
  const showsPicker = showsContractDepartment(authState.selectedOrg);
  const [departmentId, setDepartmentId] = useState(() => initialContractDepartmentId(authState.selectedOrg));

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormValues>();

  const navigateToContract = useCallback(
    (contractId: string) => {
      if (urls.contracts.edit.isCurrentPage()) {
        window.location.replace(urls.contracts.edit.go(contractId).details.fullPathName("details"));
      } else {
        navigate(urls.contracts.edit.go(contractId).details, {
          replace: true,
        });
      }
    },
    [navigate]
  );

  const submit = async (values: FormValues) => {
    if (!departmentId) return;
    const result = await create({
      depId: departmentId,
      createContractRequest: {
        title: values.title,
        projects: project ? [project.id] : undefined,
        timezone: moment.tz.guess(),
        templateId: values.template?.value ?? undefined,
      },
    });

    if ("data" in result) {
      setValue("title", "");
      toast({
        title: t("New draft created!"),
        status: "success",
        position: "top-right",
      });
      if (goToContractOnCreation) navigateToContract(result.data.id);
      else onClose();
    } else {
      apiErrorDisplayer.trigger(result.error);
    }
  };

  const getTemplateName = (template: ContractTemplateDto): string => {
    if (template.name) {
      return template.name;
    } else if (template.commonType) {
      switch (template.commonType) {
        case "ExpirationDate":
          return t("Contract with expiration date");
      }
    }

    throw new Error("Should never happen");
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <FormControl isInvalid={errors.title !== undefined} pt="2.5">
        <FormLabel htmlFor={"title"}>{t("Contract title")}</FormLabel>
        <Input
          id={"title"}
          type="title"
          backgroundColor="smBackground"
          {...register("title", {
            required: t("Please provide a title for your contract") ?? "",
            minLength: {
              value: 2,
              message: t("Must be at least 2 characters long"),
            },
          })}
        />
        <FormErrorMessage>{errors.title && errors.title.message}</FormErrorMessage>
        <FormHelperText>{t("Please provide a title for your contract")}</FormHelperText>
      </FormControl>
      {showsPicker && (
        <FormControl mt="30px">
          <FormLabel htmlFor="contract-department">{t("Department")}</FormLabel>
          <Select
            id="contract-department"
            backgroundColor="smBackground"
            value={departmentId ?? ""}
            placeholder={t("Select department") ?? undefined}
            onChange={(e) => setDepartmentId(e.target.value || undefined)}
          >
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </Select>
          <FormHelperText>{t("The contract belongs to this department")}</FormHelperText>
        </FormControl>
      )}
      <Box mt="30px">
        <SingleFormSelector
          options={templates.map((t) => ({
            label: getTemplateName(t),
            value: t.id,
          }))}
          name={"template"}
          label={t("Select type")}
          helperText={t("Select a type") ?? ""}
          control={control}
          placeholderText={""}
          noOptionsProvidedMessage={t("No types provided")}
          noMatchingOptionsMessage={t("No matching types")}
          errorMessage={undefined}
        />
      </Box>
      <Flex mt="10" justifyContent={"end"}>
        <Button type="submit" colorScheme="teal" isLoading={isLoading} isDisabled={!departmentId}>
          {t("Create contract")}
        </Button>
      </Flex>
    </form>
  );
};
